import { useCallback } from 'react';
import { dashboardApi } from '@/api/dashboard';
import { useResource } from '@/hooks/useResource';
import type { AuditLogEntry, DashboardResponse } from '@/types/dashboard';

interface DashboardData {
  stats: DashboardResponse;
  activity: AuditLogEntry[];
}

/**
 * Dashboard statistics and (for users allowed to read audit logs) recent activity
 */
export function useDashboard({ includeActivity }: { includeActivity: boolean }) {
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<DashboardData> => {
      const [stats, activity] = await Promise.all([
        dashboardApi.getStats({ signal }),
        includeActivity ? dashboardApi.getRecentActivity(10, { signal }) : Promise.resolve([]),
      ]);
      return { stats, activity };
    },
    [includeActivity]
  );

  const resource = useResource<DashboardData>(fetcher, {
    errorMessage: 'Failed to load dashboard data',
  });

  return {
    stats: resource.data?.stats ?? null,
    activity: resource.data?.activity ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload: resource.reload,
  };
}
